<template>
  <Transition :name="transitionName" appear type="animation">
    <div
      v-if="!closing"
      ref="el"
      :class="['toast', type]"
      :style="[{ right: '0', top: `${offset}px` }, swipeStyle]"
      @focusin="pause"
      @focusout="resume"
      @mousedown="startSwipe"
      @mouseenter="pause"
      @mouseleave="resume"
      @touchstart.passive="startSwipe"
    >
      <div class="toast__content-wrapper">
        <div class="toast__content">
          <div class="toast__content__text" />
        </div>
      </div>
      <div class="toast__slot-wrapper">
        <slot />
      </div>
      <button
        aria-label="Close notification"
        class="toast__close-icon"
        type="button"
        @click="emit('close')"
      />
      <div v-if="timeout > 0" :style="{ width: `${progress}%` }" class="toast__progress" />
    </div>
  </Transition>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

const props = defineProps({
  type: { type: String, default: "default" },
  timeout: { type: Number, default: 5000 },
  offset: { type: Number, required: true },
  closing: { type: Boolean, default: false },
});
const emit = defineEmits(["close"]);

const el = ref(null);
defineExpose({ el });

// Counts down while shown, paused while the pointer is over the toast.
const SWIPE_TO_CLOSE = 200;
const PAUSE_MIN_WIDTH = 425;
const progress = ref(100);
let remaining = props.timeout;
let startedAt = 0;
let interval;
let timer;
let paused = false;

const start = () => {
  if (props.timeout <= 0) return;
  startedAt = Date.now();
  interval = setInterval(
    () => {
      progress.value -= 1;
    },
    props.timeout / 100 - 5
  );
  timer = setTimeout(() => emit("close"), remaining);
};

const stop = () => {
  clearInterval(interval);
  clearTimeout(timer);
};

const pause = () => {
  if (paused || props.timeout <= 0 || window.innerWidth <= PAUSE_MIN_WIDTH) return;
  stop();
  remaining -= Date.now() - startedAt;
  paused = true;
};

// Dragged sideways by more than SWIPE_TO_CLOSE px, the toast closes.
const swiped = ref(0);
const swipeStyle = ref({});
let swipeFrom = null;

const pointerX = (event) => (event.touches ? event.touches[0].clientX : event.clientX);

const onSwipe = (event) => {
  if (swipeFrom === null) return;
  swiped.value = swipeFrom - pointerX(event);
  swipeStyle.value = { transition: "none", right: `${swiped.value}px !important` };
  if (Math.abs(swiped.value) > SWIPE_TO_CLOSE) emit("close");
};

const endSwipe = () => {
  swipeFrom = null;
  swipeStyle.value = { transition: "right .3s ease-out", right: 0 };
  window.removeEventListener("mousemove", onSwipe);
  window.removeEventListener("touchmove", onSwipe);
};

const startSwipe = (event) => {
  swipeFrom = pointerX(event);
  const touch = Boolean(event.touches);
  window.addEventListener(touch ? "touchmove" : "mousemove", onSwipe);
  window.addEventListener(touch ? "touchend" : "mouseup", endSwipe, { once: true });
};

const resume = () => {
  window.removeEventListener("mousemove", onSwipe);
  if (paused) {
    paused = false;
    start();
  }
};

const transitionName = computed(() => {
  if (swiped.value > SWIPE_TO_CLOSE) return "toast__fade-out-left";
  if (swiped.value < -SWIPE_TO_CLOSE) return "toast__fade-out-right";
  return "toast__bounce";
});

onMounted(start);
onBeforeUnmount(() => {
  stop();
  endSwipe();
});
</script>

<style>
/* Look and motion of the mosha-vue-toastify toasts this replaces. */
.toast {
  touch-action: none;
  display: flex;
  justify-content: space-between;
  position: fixed;
  min-height: 64px;
  max-height: 800px;
  box-sizing: border-box;
  overflow: hidden;
  padding: 12px 8px;
  word-break: break-word;
  min-width: 312px;
  max-width: 480px;
  z-index: 9999;
  width: max-content;
  transition:
    top 0.3s ease-out 0.5s,
    bottom 0.3s ease-out 0.5s;
  border-radius: 8px;
  box-shadow:
    0 1px 10px 0 rgba(0, 0, 0, 0.1),
    0 2px 15px 0 rgba(0, 0, 0, 0.05);
  margin: 0 16px;
  -webkit-touch-callout: none;
  user-select: none;
}

.toast__content-wrapper {
  display: flex;
  align-items: center;
}

.toast__content {
  font-family: sans-serif;
  line-height: 20px;
  display: flex;
  flex-direction: column;
  color: #fff;
}

.toast__content__text {
  margin-bottom: 2px;
  font-size: 16px;
  font-weight: 700;
}

.toast__slot-wrapper {
  width: 100%;
}

.toast__close-icon::before {
  font-size: 20px;
  cursor: pointer;
  content: "×";
  color: #fff;
  transition: color 0.3s;
  font-weight: 600;
  margin-left: 10px;
  position: relative;
  top: -12px;
}

.toast__close-icon:hover::before {
  color: grey;
}

.toast__progress {
  transition: all linear 0.2s;
  position: absolute;
  z-index: 9999;
  height: 8px;
  background-color: rgba(255, 255, 255, 0.6);
  bottom: 0;
  margin-left: -8px;
}

.toast.success {
  background-color: #06d6a0;
}

.toast.warning {
  background-color: #ffc43d;
}

.toast.info {
  background-color: #1b9aaa;
}

.toast.danger {
  background-color: #ef476f;
}

.toast.default {
  background-color: #fff;
}

.toast.default .toast__content {
  color: #616161;
}

.toast.default .toast__close-icon::before {
  color: #616161;
}

.toast.default .toast__close-icon:hover::before {
  color: #d0d4d4;
}

.toast.default .toast__progress {
  background-image: linear-gradient(-225deg, #69eacb 0, #eaccf8 48%, #6654f1 100%);
}

@media only screen and (max-width: 475px) {
  .toast {
    max-width: 95.2%;
    left: 0;
    right: 0;
    margin: 0 auto;
  }
}

.toast__bounce-enter-active {
  animation: toast-bounce-in-right 0.7s;
}

.toast__bounce-leave-active {
  animation: toast-bounce-out-right 0.7s;
}

.toast__fade-out-left-leave-active {
  animation: toast-fade-out-left 0.5s;
}

.toast__fade-out-right-leave-active {
  animation: toast-fade-out-right 0.5s;
}

@keyframes toast-bounce-in-right {
  60%,
  75%,
  90%,
  from,
  to {
    animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
  }

  from {
    opacity: 0;
    transform: translate3d(2000px, 0, 0);
  }

  60% {
    opacity: 1;
    transform: translate3d(-25px, 0, 0);
  }

  75% {
    transform: translate3d(10px, 0, 0);
  }

  90% {
    transform: translate3d(-5px, 0, 0);
  }

  to {
    transform: none;
  }
}

@keyframes toast-bounce-out-right {
  20% {
    opacity: 1;
    transform: translate3d(-20px, 0, 0);
  }

  to {
    opacity: 0;
    transform: translate3d(2000px, 0, 0);
  }
}

@keyframes toast-fade-out-left {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
    transform: translate3d(-100%, 0, 0);
  }
}

@keyframes toast-fade-out-right {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
    transform: translate3d(100%, 0, 0);
  }
}
</style>
